import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  AppBar,
  Toolbar,
  Avatar
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pendingPatientsService, PendingPatient } from '../services/pendingPatientsService';

const PendingPatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingPatients, setPendingPatients] = useState<PendingPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    patient: PendingPatient | null;
    action: 'APPROVE' | 'REJECT';
  }>({ open: false, patient: null, action: 'APPROVE' });

  // Check if user is a provider
  useEffect(() => {
    if (user?.userType !== 'provider') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchPendingPatients();
  }, [page, rowsPerPage]);

  const fetchPendingPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await pendingPatientsService.getPendingPatients(page, rowsPerPage);
      setPendingPatients(response.content);
      setTotalElements(response.totalElements);
    } catch (error: any) {
      setError(error.message || 'Failed to fetch pending patients');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionClick = (patient: PendingPatient, action: 'APPROVE' | 'REJECT') => {
    setConfirmDialog({ open: true, patient, action });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.patient) return;

    try {
      setLoading(true);
      if (confirmDialog.action === 'APPROVE') {
        await pendingPatientsService.approvePatient(confirmDialog.patient.id);
        setSuccess(`Patient ${confirmDialog.patient.firstName} ${confirmDialog.patient.lastName} has been approved successfully.`);
      } else {
        await pendingPatientsService.rejectPatient(confirmDialog.patient.id);
        setSuccess(`Patient ${confirmDialog.patient.firstName} ${confirmDialog.patient.lastName} has been rejected.`);
      }
      setConfirmDialog({ open: false, patient: null, action: 'APPROVE' });
      fetchPendingPatients(); // Refresh the list
    } catch (error: any) {
      setError(error.message || `Failed to ${confirmDialog.action.toLowerCase()} patient`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, patient: null, action: 'APPROVE' });
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Pending Patients
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Pending Patient Approvals
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Review and manage patient requests waiting for your approval
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Paper elevation={2}>
          {loading && pendingPatients.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : pendingPatients.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                No pending patients at this time
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                All patient requests have been processed
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>School ID</TableCell>
                      <TableCell>Requested Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingPatients.map((patient) => (
                      <TableRow key={patient.id} hover>
                        <TableCell>
                          <Typography variant="body1">
                            {patient.firstName} {patient.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>{patient.email}</TableCell>
                        <TableCell>{patient.phone || 'N/A'}</TableCell>
                        <TableCell>{patient.schoolId || 'N/A'}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {pendingPatientsService.formatDate(patient.requestedAt)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(patient.requestedAt).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<ApproveIcon />}
                              onClick={() => handleActionClick(patient, 'APPROVE')}
                              disabled={loading}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<RejectIcon />}
                              onClick={() => handleActionClick(patient, 'REJECT')}
                              disabled={loading}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </Paper>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
          <DialogTitle>
            Confirm {confirmDialog.action === 'APPROVE' ? 'Approval' : 'Rejection'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to {confirmDialog.action.toLowerCase()} patient{' '}
              <strong>
                {confirmDialog.patient?.firstName} {confirmDialog.patient?.lastName}
              </strong>
              ?
              {confirmDialog.action === 'APPROVE' && (
                <Box sx={{ mt: 2 }}>
                  This patient will be added to your patient list and will be able to access the system.
                </Box>
              )}
              {confirmDialog.action === 'REJECT' && (
                <Box sx={{ mt: 2 }}>
                  This patient's request will be denied and they will not be able to access the system.
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              color={confirmDialog.action === 'APPROVE' ? 'success' : 'error'}
              variant="contained"
              disabled={loading}
              autoFocus
            >
              {loading ? <CircularProgress size={20} /> : confirmDialog.action}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default PendingPatientsPage;